import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { Plus, Pencil, Trash2, CheckCircle2, XCircle, RefreshCw, Ban, Building2, UserPlus, ArrowLeft, ShieldCheck, Users } from "lucide-react";

const LINK_STATUS_MAP = {
  pending:   { label: "Pendente",  cls: "bg-amber-100 text-amber-800" },
  active:    { label: "Ativo",     cls: "bg-emerald-100 text-emerald-800" },
  suspended: { label: "Suspenso",  cls: "bg-slate-200 text-slate-700" },
  revoked:   { label: "Revogado",  cls: "bg-red-100 text-red-800" },
};

const parseCsv = (raw) => (raw || "").split(",").map((s) => s.trim()).filter(Boolean);
const csvOf = (arr) => (Array.isArray(arr) ? arr.join(", ") : "");

// ============================================ Modal: New/Edit Contract
const ContractModal = ({ open, contract, insurers, onClose, onSaved }) => {
  const [form, setForm] = useState({});
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState(false);
  const editing = !!contract;

  useEffect(() => {
    if (!open) return;
    setForm(contract ? {
      ...contract,
      _plan_ids_csv: (contract.plan_ids || []).join(","),
      _specialties_csv: csvOf(contract.specialties),
      _procedure_codes_csv: csvOf(contract.procedure_codes),
    } : {
      insurer_id: "", contract_number: "", active: true, teleconsultation_enabled: true,
      valid_from: "", valid_until: "",
      _plan_ids_csv: "", _specialties_csv: "", _procedure_codes_csv: "",
      notes: "",
    });
  }, [open, contract]);

  useEffect(() => {
    if (!form.insurer_id) { setPlans([]); return; }
    api.get(`/admin/convenios/plans`, { params: { insurer_id: form.insurer_id } })
      .then((r) => setPlans(r.data || []))
      .catch(() => setPlans([]));
  }, [form.insurer_id]);

  if (!open) return null;
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const toggleId = (arr, id) => (arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id]);
  const currentPlanIds = parseCsv(form._plan_ids_csv);

  const save = async () => {
    if (!form.insurer_id) return toast.error("Selecione a operadora");
    setBusy(true);
    try {
      const payload = {
        insurer_id: form.insurer_id,
        contract_number: (form.contract_number || "").trim() || null,
        active: !!form.active,
        teleconsultation_enabled: !!form.teleconsultation_enabled,
        valid_from: form.valid_from || null,
        valid_until: form.valid_until || null,
        plan_ids: parseCsv(form._plan_ids_csv),
        specialties: parseCsv(form._specialties_csv),
        procedure_codes: parseCsv(form._procedure_codes_csv),
        notes: (form.notes || "").trim() || null,
      };
      if (editing) {
        const patch = { ...payload };
        delete patch.insurer_id;  // não é editável
        await api.patch(`/admin/convenios/contracts/${contract.id}`, patch);
      } else {
        await api.post(`/admin/convenios/contracts`, payload);
      }
      toast.success(editing ? "Contrato atualizado" : "Contrato criado");
      onSaved?.(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto"
      onClick={onClose} data-testid="contract-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full my-4" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-slate-200">
          <h3 className="font-bold text-lg text-[#0B2B62]">
            {editing ? "Editar contrato RAF↔operadora" : "Novo contrato RAF↔operadora"}
          </h3>
          <p className="text-xs text-slate-500 mt-1">
            Modelo institucional: RAF Direct assina esse contrato e vincula profissionais autorizados a atender por ele.
          </p>
        </div>
        <div className="p-5 space-y-3">
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Operadora</span>
              <select value={form.insurer_id || ""} onChange={(e) => set("insurer_id", e.target.value)}
                disabled={editing}
                data-testid="contract-insurer"
                className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm bg-white disabled:bg-slate-50">
                <option value="">—</option>
                {insurers.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Nº contrato</span>
              <input value={form.contract_number || ""} onChange={(e) => set("contract_number", e.target.value)}
                data-testid="contract-number"
                className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
            </label>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Válido a partir</span>
              <input type="date" value={(form.valid_from || "").slice(0, 10)}
                onChange={(e) => set("valid_from", e.target.value ? `${e.target.value}T00:00:00Z` : "")}
                className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Válido até</span>
              <input type="date" value={(form.valid_until || "").slice(0, 10)}
                onChange={(e) => set("valid_until", e.target.value ? `${e.target.value}T23:59:59Z` : "")}
                className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
            </label>
          </div>
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Planos cobertos</span>
            <p className="text-[10px] text-slate-500 mb-1.5">Vazio = cobre todos os planos da operadora.</p>
            {plans.length > 0 ? (
              <div className="flex flex-wrap gap-1.5">
                {plans.map((p) => {
                  const on = currentPlanIds.includes(p.id);
                  return (
                    <button key={p.id} type="button"
                      onClick={() => set("_plan_ids_csv", toggleId(currentPlanIds, p.id).join(","))}
                      data-testid={`contract-plan-${p.id}`}
                      className={`text-xs px-2.5 py-1 rounded-full border transition ${on
                        ? "bg-emerald-600 text-white border-emerald-700"
                        : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"}`}>
                      {p.name}
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="text-xs text-slate-500">Selecione uma operadora para listar os planos.</div>
            )}
          </label>
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Especialidades (CSV)</span>
              <input value={form._specialties_csv || ""} onChange={(e) => set("_specialties_csv", e.target.value)}
                placeholder="Psicologia, Psiquiatria"
                className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Códigos TUSS/AMB (CSV)</span>
              <input value={form._procedure_codes_csv || ""} onChange={(e) => set("_procedure_codes_csv", e.target.value)}
                placeholder="50000462, 50000470"
                className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm font-mono" />
            </label>
          </div>
          <div className="flex flex-wrap gap-4">
            <label className="inline-flex items-center gap-2 text-sm">
              <input type="checkbox" checked={!!form.active} onChange={(e) => set("active", e.target.checked)}
                data-testid="contract-active"
                className="h-4 w-4 accent-emerald-700" />
              Contrato ativo
            </label>
            <label className="inline-flex items-center gap-2 text-sm">
              <input type="checkbox" checked={!!form.teleconsultation_enabled} onChange={(e) => set("teleconsultation_enabled", e.target.checked)}
                data-testid="contract-tele"
                className="h-4 w-4 accent-emerald-700" />
              Teleconsulta habilitada
            </label>
          </div>
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Notas internas</span>
            <textarea rows={2} value={form.notes || ""} onChange={(e) => set("notes", e.target.value)}
              className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm resize-none" />
          </label>
        </div>
        <div className="p-5 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-md border border-slate-300 text-sm">Cancelar</button>
          <button onClick={save} disabled={busy} data-testid="contract-save"
            className="px-4 py-2 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold disabled:opacity-50">
            {busy ? "Salvando…" : (editing ? "Salvar" : "Criar contrato")}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================ Modal: Add Link (professional)
const LinkModal = ({ open, contract, existingIds, onClose, onSaved }) => {
  const [profs, setProfs] = useState([]);
  const [q, setQ] = useState("");
  const [selectedProId, setSelectedProId] = useState("");
  const [form, setForm] = useState({
    status: "active", authorized_specialties: "", authorized_procedure_codes: "",
    valid_from: "", valid_until: "", notes: "",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/admin/professionals").then((r) => setProfs(r.data || [])).catch(() => setProfs([]));
    setSelectedProId(""); setQ("");
    setForm({ status: "active", authorized_specialties: "", authorized_procedure_codes: "",
              valid_from: "", valid_until: "", notes: "" });
  }, [open]);

  const filteredProfs = useMemo(() => {
    const term = q.trim().toLowerCase();
    return profs
      .filter((p) => !existingIds.has(p.id))
      .filter((p) => !term || (p.name || p.display_name || "").toLowerCase().includes(term)
        || (p.email || "").toLowerCase().includes(term))
      .slice(0, 40);
  }, [profs, q, existingIds]);

  if (!open) return null;
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const save = async () => {
    if (!selectedProId) return toast.error("Selecione um profissional");
    setBusy(true);
    try {
      await api.post(`/admin/convenios/contracts/${contract.id}/professionals`, {
        professional_id: selectedProId,
        status: form.status,
        authorized_specialties: parseCsv(form.authorized_specialties),
        authorized_procedure_codes: parseCsv(form.authorized_procedure_codes),
        valid_from: form.valid_from || null,
        valid_until: form.valid_until || null,
        notes: (form.notes || "").trim() || null,
      });
      toast.success("Profissional vinculado");
      onSaved?.(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto"
      onClick={onClose} data-testid="contract-link-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-xl w-full my-4" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-slate-200">
          <h3 className="font-bold text-lg text-[#0B2B62]">Vincular profissional</h3>
          <p className="text-xs text-slate-500 mt-0.5">Contrato #{contract?.contract_number || contract?.id}</p>
        </div>
        <div className="p-5 space-y-3">
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Buscar profissional</span>
            <input value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Nome ou e-mail"
              data-testid="link-search"
              className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
          </label>
          <div className="border border-slate-200 rounded-lg max-h-56 overflow-y-auto">
            {filteredProfs.length === 0 ? (
              <div className="text-xs text-slate-500 p-3">Nenhum profissional encontrado.</div>
            ) : filteredProfs.map((p) => (
              <button key={p.id} type="button"
                onClick={() => setSelectedProId(p.id)}
                data-testid={`link-pro-${p.id}`}
                className={`w-full text-left px-3 py-2 text-sm border-b last:border-b-0 hover:bg-slate-50 ${selectedProId === p.id ? "bg-emerald-50" : ""}`}>
                <div className="font-semibold text-slate-800">{p.display_name || p.name}</div>
                <div className="text-[11px] text-slate-500">{p.email}</div>
              </button>
            ))}
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Status</span>
              <select value={form.status} onChange={(e) => set("status", e.target.value)}
                className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm bg-white">
                {Object.entries(LINK_STATUS_MAP).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Válido até</span>
              <input type="date" value={(form.valid_until || "").slice(0, 10)}
                onChange={(e) => set("valid_until", e.target.value ? `${e.target.value}T23:59:59Z` : "")}
                className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
            </label>
          </div>
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Especialidades autorizadas (CSV)</span>
            <input value={form.authorized_specialties} onChange={(e) => set("authorized_specialties", e.target.value)}
              placeholder="Psicologia"
              className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
          </label>
        </div>
        <div className="p-5 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-md border border-slate-300 text-sm">Cancelar</button>
          <button onClick={save} disabled={busy} data-testid="link-save"
            className="px-4 py-2 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold disabled:opacity-50">
            {busy ? "Vinculando…" : "Vincular"}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================ Detail view: linked pros
const ContractDetailView = ({ contract, insurers, onBack }) => {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAdd, setShowAdd] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/convenios/contracts/${contract.id}/professionals`);
      setLinks(data.links || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
    finally { setLoading(false); }
  }, [contract.id]);

  useEffect(() => { load(); }, [load]);

  const changeStatus = async (l, status) => {
    try {
      await api.patch(`/admin/convenios/contracts/${contract.id}/professionals/${l.id}`, { status });
      toast.success("Vínculo atualizado");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
  };

  const remove = async (l) => {
    if (!window.confirm(`Remover vínculo de ${l.professional_name}?`)) return;
    try {
      await api.delete(`/admin/convenios/contracts/${contract.id}/professionals/${l.id}`);
      toast.success("Vínculo removido");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
  };

  const insurer = insurers.find((i) => i.id === contract.insurer_id);
  const existingIds = new Set(links.filter((l) => ["pending", "active"].includes(l.status)).map((l) => l.professional_id));

  return (
    <div className="space-y-3" data-testid="contract-detail">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <button onClick={onBack} className="p-1.5 text-slate-500 hover:text-slate-800 border border-slate-200 rounded-md">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-bold">Contrato institucional</div>
            <div className="font-bold text-[#0B2B62]">{insurer?.name || "—"} · #{contract.contract_number || contract.id.slice(0, 8)}</div>
          </div>
        </div>
        <button onClick={() => setShowAdd(true)}
          data-testid="contract-add-link"
          className="text-xs px-3 py-1.5 bg-amber-400 hover:bg-amber-300 text-[#0B2B62] font-semibold rounded-md inline-flex items-center gap-1.5">
          <UserPlus className="w-3.5 h-3.5" /> Vincular profissional
        </button>
      </div>
      {loading ? <div className="p-6 text-slate-500 text-sm">Carregando…</div>
      : links.length === 0 ? (
        <div className="p-8 border border-dashed border-slate-300 rounded-xl bg-slate-50 text-slate-500 text-sm text-center">
          Nenhum profissional vinculado ainda.
        </div>
      ) : (
        <div className="border border-slate-200 rounded-xl bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-500">
              <tr>
                <th className="text-left px-4 py-3">Profissional</th>
                <th className="text-left px-4 py-3">Vínculo</th>
                <th className="text-left px-4 py-3">Ativo agora?</th>
                <th className="text-left px-4 py-3">Especialidades</th>
                <th className="text-right px-4 py-3">Ações</th>
              </tr>
            </thead>
            <tbody>
              {links.map((l) => {
                const s = LINK_STATUS_MAP[l.status] || { label: l.status, cls: "bg-slate-100 text-slate-700" };
                return (
                  <tr key={l.id} className="border-t" data-testid={`link-row-${l.id}`}>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-[#0B2B62]">{l.professional_name || "—"}</div>
                      <div className="text-[11px] text-slate-500">{l.professional_email}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase ${s.cls}`}>{s.label}</span>
                    </td>
                    <td className="px-4 py-3">
                      {l.is_active_now ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <XCircle className="w-4 h-4 text-slate-400" />}
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-600">
                      {(l.authorized_specialties || []).join(", ") || <span className="text-slate-400">Todas</span>}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex gap-1.5 justify-end">
                        {l.status === "active" && (
                          <button onClick={() => changeStatus(l, "suspended")}
                            data-testid={`link-suspend-${l.id}`}
                            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold text-slate-700 border border-slate-300 rounded-md hover:bg-slate-50">
                            <Ban className="w-3.5 h-3.5" /> Suspender
                          </button>
                        )}
                        {(l.status === "suspended" || l.status === "pending") && (
                          <button onClick={() => changeStatus(l, "active")}
                            data-testid={`link-activate-${l.id}`}
                            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold text-emerald-700 border border-emerald-300 rounded-md hover:bg-emerald-50">
                            <CheckCircle2 className="w-3.5 h-3.5" /> Ativar
                          </button>
                        )}
                        {l.status !== "revoked" && (
                          <button onClick={() => changeStatus(l, "revoked")}
                            data-testid={`link-revoke-${l.id}`}
                            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold text-red-700 border border-red-200 rounded-md hover:bg-red-50">
                            <XCircle className="w-3.5 h-3.5" /> Revogar
                          </button>
                        )}
                        <button onClick={() => remove(l)}
                          data-testid={`link-delete-${l.id}`}
                          className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold text-red-600 border border-red-200 rounded-md hover:bg-red-50">
                          <Trash2 className="w-3.5 h-3.5" /> Excluir
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <LinkModal open={showAdd} contract={contract} existingIds={existingIds}
        onClose={() => setShowAdd(false)} onSaved={load} />
    </div>
  );
};

// ============================================ Main: Contracts Sub-tab
export const ContractsSubTab = ({ insurers }) => {
  const [contracts, setContracts] = useState([]);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter ? { insurer_id: filter } : {};
      const { data } = await api.get("/admin/convenios/contracts", { params });
      setContracts(data || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const remove = async (c) => {
    if (!window.confirm(`Excluir contrato #${c.contract_number || c.id.slice(0, 8)}?`)) return;
    try {
      await api.delete(`/admin/convenios/contracts/${c.id}`);
      toast.success("Contrato excluído");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
  };

  if (detail) {
    return <ContractDetailView contract={detail} insurers={insurers}
      onBack={() => { setDetail(null); load(); }} />;
  }

  return (
    <div className="space-y-3" data-testid="admin-contracts-tab">
      <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-xs text-emerald-900">
        <ShieldCheck className="w-3.5 h-3.5 inline mr-1" />
        <b>Modelo Institucional (B) — prioritário:</b> a RAF Direct assina o contrato com a
        operadora, define planos/especialidades cobertos e vincula profissionais autorizados.
        No booking, o resolver escolhe automaticamente entre este modelo e o credenciamento
        individual (modelo A); o paciente vê apenas <b>"Disponível pelo seu convênio"</b>.
      </div>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <select value={filter} onChange={(e) => setFilter(e.target.value)}
            data-testid="contracts-filter-insurer"
            className="px-3 py-2 border border-slate-300 rounded-md text-sm bg-white">
            <option value="">Todas as operadoras ({contracts.length})</option>
            {insurers.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
          </select>
          <button onClick={load}
            data-testid="contracts-refresh"
            className="p-2 border border-slate-200 rounded-md text-slate-600 hover:bg-slate-50">
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
        <button onClick={() => setCreating(true)}
          data-testid="admin-contracts-new"
          className="text-xs px-3 py-1.5 bg-amber-400 hover:bg-amber-300 text-[#0B2B62] font-semibold rounded-md inline-flex items-center gap-1.5">
          <Plus className="w-3.5 h-3.5" /> Novo contrato
        </button>
      </div>
      {loading ? <div className="p-6 text-slate-500 text-sm">Carregando…</div>
      : contracts.length === 0 ? (
        <div className="p-8 border border-dashed border-slate-300 rounded-xl bg-slate-50 text-slate-500 text-sm text-center">
          Nenhum contrato cadastrado {filter ? "pra essa operadora" : "ainda"}.
        </div>
      ) : (
        <div className="border border-slate-200 rounded-xl bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-500">
              <tr>
                <th className="text-left px-4 py-3">Operadora</th>
                <th className="text-left px-4 py-3">Nº contrato</th>
                <th className="text-left px-4 py-3">Validade</th>
                <th className="text-left px-4 py-3">Ativo</th>
                <th className="text-left px-4 py-3">Tele</th>
                <th className="text-left px-4 py-3">Vínculos</th>
                <th className="text-right px-4 py-3">Ações</th>
              </tr>
            </thead>
            <tbody>
              {contracts.map((c) => (
                <tr key={c.id} className="border-t" data-testid={`contract-row-${c.id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      <Building2 className="w-3.5 h-3.5 text-slate-400" />
                      <span className="font-semibold text-[#0B2B62]">{c.insurer_name || "—"}</span>
                    </div>
                    <div className="text-[11px] text-slate-500">
                      Planos: {c.plan_ids?.length ? `${c.plan_ids.length} específico(s)` : "Todos"}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-700 text-xs font-mono">{c.contract_number || <span className="text-slate-400">—</span>}</td>
                  <td className="px-4 py-3 text-xs text-slate-600">
                    {(c.valid_from || "").slice(0, 10) || "—"} → {(c.valid_until || "").slice(0, 10) || <b className="text-emerald-700">∞</b>}
                  </td>
                  <td className="px-4 py-3">
                    {c.is_active_now
                      ? <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 font-bold uppercase">Sim</span>
                      : <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-200 text-slate-600 font-bold uppercase">Não</span>}
                  </td>
                  <td className="px-4 py-3">
                    {c.teleconsultation_enabled ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <XCircle className="w-4 h-4 text-slate-400" />}
                  </td>
                  <td className="px-4 py-3">
                    <button onClick={() => setDetail(c)}
                      data-testid={`contract-view-${c.id}`}
                      className="inline-flex items-center gap-1 text-xs px-2.5 py-1 border border-slate-300 rounded-md hover:bg-slate-50">
                      <Users className="w-3.5 h-3.5 text-emerald-600" />
                      <b>{c.linked_professionals_count || 0}</b>
                    </button>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-1.5 justify-end">
                      <button onClick={() => setEditing(c)}
                        data-testid={`contract-edit-${c.id}`}
                        className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold text-slate-700 border border-slate-300 rounded-md hover:bg-slate-50">
                        <Pencil className="w-3.5 h-3.5" /> Editar
                      </button>
                      <button onClick={() => remove(c)}
                        data-testid={`contract-delete-${c.id}`}
                        className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold text-red-600 border border-red-200 rounded-md hover:bg-red-50">
                        <Trash2 className="w-3.5 h-3.5" /> Excluir
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <ContractModal open={creating} insurers={insurers} onClose={() => setCreating(false)} onSaved={load} />
      <ContractModal open={!!editing} contract={editing} insurers={insurers}
        onClose={() => setEditing(null)} onSaved={load} />
    </div>
  );
};

export default ContractsSubTab;
